/**
 * Navbar Component
 *
 * Navigation bar with:
 * - Navigation links
 * - User menu
 * - Logout button
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronDown, LogOut } from 'lucide-react';

/**
 * Helper function for NavLink styling
 */
const getNavLinkClasses = (isActive: boolean): string => {
  const baseClasses = 'inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors';
  const activeClasses = 'border-primary text-foreground';
  const inactiveClasses = 'border-transparent text-muted-foreground hover:border-border hover:text-foreground';

  return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`;
};

export const Navbar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      // Error is already logged in AuthContext
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <nav className="bg-card border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/dashboard" className="text-xl font-bold text-primary">
                Mindspace Dashboard
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <NavLink to="/dashboard" className={({ isActive }) => getNavLinkClasses(isActive)}>
                Dashboard
              </NavLink>
              <NavLink to="/workflows" className={({ isActive }) => getNavLinkClasses(isActive)}>
                Workflows
              </NavLink>
              {/* Hide Analytics for Platform Admin */}
              {user?.tenantId !== '00000000-0000-0000-0000-000000000001' && (
                <NavLink to="/analytics" className={({ isActive }) => getNavLinkClasses(isActive)}>
                  Analytics
                </NavLink>
              )}
              <NavLink to="/settings" className={({ isActive }) => getNavLinkClasses(isActive)}>
                Settings
              </NavLink>
              {/* Platform Admin Only: Metrics */}
              {user?.role === 'admin' && user?.tenantId === '00000000-0000-0000-0000-000000000001' && (
                <NavLink to="/admin/metrics" className={({ isActive }) => getNavLinkClasses(isActive)}>
                  Admin: Metrics
                </NavLink>
              )}
              {/* All Tenant Admins: Workflow Registry */}
              {user?.role === 'admin' && (
                <NavLink to="/admin/workflow-registry" className={({ isActive }) => getNavLinkClasses(isActive)}>
                  Workflows
                </NavLink>
              )}
            </div>
          </div>
          <div className="flex items-center">
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary rounded-md px-2 py-1"
              >
                <span className="text-muted-foreground mr-2">{user?.email}</span>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </button>

              {showUserMenu && (
                <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-lg border border-border bg-card focus:outline-none z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 text-sm text-foreground border-b border-border">
                      <div className="font-medium">{user?.email}</div>
                      <div className="text-xs text-muted-foreground">{user?.role}</div>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full text-left px-4 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
